<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="flex flex-column gap-3">
    <div class="form-field">
      <label class="form-label" for="name">{{ $t("natureForm.name") }}</label>
      <PrimeInputText id="name" v-model="editableNature.name" class="w-full" />
    </div>

    <div class="form-field">
      <label class="form-label" for="code">{{ $t("natureForm.code") }}</label>
      <PrimeInputText
        id="code"
        v-model="editableNature.code"
        :disabled="isEditMode && !isDuplicating"
        class="w-full"
      />
    </div>

    <div class="form-field">
      <label class="form-label" for="type">{{
        $t("natureForm.documentType")
      }}</label>
      <PrimeInputText id="type" v-model="editableNature.type" class="w-full" />
    </div>

    <div class="form-field">
      <label class="form-label" for="aspects">{{
        $t("natureForm.aspects")
      }}</label>
      <PrimeMultiSelect
        id="aspects"
        v-model="selectedAspects"
        :options="availableAspects"
        :placeholder="$t('natureForm.selectAspects')"
        optionLabel="title"
        optionValue="id"
        :filterFields="['id', 'title']"
        class="w-full"
        filter
      >
        <template #option="slotProps">
          <div>
            <div class="font-semibold">{{ slotProps.option.id }}</div>
            <small class="text-color-secondary">{{
              slotProps.option.title
            }}</small>
          </div>
        </template>
      </PrimeMultiSelect>
    </div>

    <div class="form-field">
      <label class="form-label" for="icon">{{ $t("natureForm.icon") }}</label>
      <PrimeDropdown
        id="icon"
        v-model="editableNature.icon"
        :options="remixIcons"
        :placeholder="$t('natureForm.chooseIcon')"
        class="w-full"
      >
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex align-items-center">
            <i :class="slotProps.value" class="mr-2" aria-hidden="true"></i>
            <span>{{ slotProps.value }}</span>
          </div>
        </template>
        <template #option="slotProps">
          <div class="flex align-items-center">
            <i :class="slotProps.option" class="mr-2" aria-hidden="true"></i>
            <span>{{ slotProps.option }}</span>
          </div>
        </template>
      </PrimeDropdown>
    </div>

    <div v-if="categories.length > 0" class="form-field">
      <label class="form-label" for="categories">{{
        $t("natureForm.categories")
      }}</label>
      <PrimeMultiSelect
        id="categories"
        v-model="editableNature.categories"
        :options="categories"
        :placeholder="$t('natureForm.selectCategories')"
        optionLabel="label"
        optionValue="id"
        class="w-full"
        filter
      />
    </div>

    <div class="form-field">
      <label class="form-label" for="emplacement">{{
        $t("natureForm.emplacement")
      }}</label>
      <PrimeMultiSelect
        id="emplacement"
        v-model="editableNature.emplacement"
        :options="emplacementOptions"
        :placeholder="$t('natureForm.selectPath')"
        filter
        autoFilterFocus
        class="w-full"
      />
      <small class="form-help"
        >{{ $t("natureForm.calculatedPath") }} :
        <code>{{ calculatedPath }}</code></small
      >
    </div>

    <div class="form-field">
      <label class="form-label">{{ $t("natureForm.metadata") }}</label>
      <div
        v-if="availableProperties.length === 0"
        class="text-center p-3 text-color-secondary"
      >
        {{ $t("natureForm.noMetadata") }}
      </div>
      <PrimeDataTable
        v-else
        :value="editableMetadata"
        stripedRows
        size="small"
        class="metadata-table"
      >
        <PrimeColumn field="key" :header="$t('natureForm.key')">
          <template #body="{ data }">
            <strong>{{ data.key }}</strong>
            <div class="form-help">{{ data.title }}</div>
          </template>
        </PrimeColumn>
        <PrimeColumn
          :header="$t('natureForm.mappingType')"
          headerStyle="width: 10rem"
        >
          <template #body="{ data }">
            <PrimeDropdown
              v-model="data.type"
              :options="metadataTypes"
              :placeholder="$t('natureForm.mappingType')"
              class="w-full"
              @change="onMetadataTypeChange(data)"
            />
          </template>
        </PrimeColumn>
        <PrimeColumn :header="$t('natureForm.value')">
          <template #body="{ data }">
            <PrimeInputText
              v-if="data.type !== 'parent' && data.type !== 'ignored'"
              v-model="data.value"
              :placeholder="$t('natureForm.valueOrFieldName')"
              class="w-full"
            />
          </template>
        </PrimeColumn>
        <PrimeColumn
          :header="$t('natureForm.required')"
          headerStyle="width: 5rem; text-align: center"
          bodyStyle="text-align: center"
        >
          <template #body="{ data }">
            <span
              v-if="
                data.type !== 'parent' &&
                data.type !== 'template' &&
                data.type !== 'ignored'
              "
              v-tooltip.top="
                isFieldOptional(data)
                  ? $t('natureForm.optionalFieldTooltip')
                  : null
              "
            >
              <PrimeInputSwitch
                v-model="data.required"
                :disabled="isFieldOptional(data)"
              />
            </span>
          </template>
        </PrimeColumn>
      </PrimeDataTable>
    </div>
  </div>
</template>

<script>
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import Dropdown from "primevue/dropdown";
import InputSwitch from "primevue/inputswitch";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tooltip from "primevue/tooltip";
import { useToast } from "primevue/usetoast";
import alfrescoModelService from "@/services/AlfrescoModelService.js";
import { useI18n } from "vue-i18n";

const newNatureTemplate = () => ({
  code: "",
  name: "",
  type: "cm:content",
  icon: "ri-file-line",
  emplacement: [],
  aspects: [],
  categories: [],
  metadata: {},
});

export default {
  name: "NatureForm",
  components: {
    PrimeInputText: InputText,
    PrimeMultiSelect: MultiSelect,
    PrimeDropdown: Dropdown,
    PrimeInputSwitch: InputSwitch,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    nature: { type: Object, default: null },
    emplacements: { type: Array, required: true },
    existingNatures: { type: Array, default: () => [] },
    isDuplicating: { type: Boolean, default: false },
    categories: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
  },
  emits: ["save", "cancel"],
  setup() {
    const toast = useToast();
    const { t } = useI18n();
    return { toast, t };
  },
  data() {
    return {
      editableNature: newNatureTemplate(),
      editableMetadata: [],
      isEditMode: false,
      metadataTypes: ["ignored", "value", "field", "template", "parent"],
      remixIcons: [
        "ri-file-line",
        "ri-file-text-line",
        "ri-file-pdf-line",
        "ri-image-line",
        "ri-film-line",
        "ri-folder-line",
        "ri-archive-line",
        "ri-mail-line",
      ],
      availableProperties: [],
      filteredProperties: [],
      selectedAspects: [],
      availableAspects: [],
    };
  },
  computed: {
    emplacementOptions() {
      return this.emplacements.map((e) => e.code);
    },
    calculatedPath() {
      if (
        !this.editableNature.emplacement ||
        this.editableNature.emplacement.length === 0
      ) {
        return "/";
      }
      return (
        "/" +
        this.editableNature.emplacement
          .map((code) => {
            const emp = this.emplacements.find((e) => e.code === code);
            if (!emp) {
              return `[${code}]`;
            }
            const cmName = emp.metadata?.["cm:name"];
            return cmName?.value || cmName?.template || code;
          })
          .join("/")
      );
    },
  },
  watch: {
    nature: {
      handler(newVal) {
        if (newVal && newVal.code) {
          this.isEditMode = true;
          this.editableNature = JSON.parse(JSON.stringify(newVal));
        } else {
          this.isEditMode = false;
          this.editableNature = newVal
            ? JSON.parse(JSON.stringify(newVal))
            : newNatureTemplate();
        }
        // Restore selected aspects from nature
        this.selectedAspects = this.editableNature.aspects || [];
        this.metadataToObjectArray(this.editableNature.metadata);
      },
      immediate: true,
    },
    "editableNature.type": {
      handler(newType) {
        if (newType) {
          this.loadAllProperties();
        }
      },
      immediate: true,
    },
    selectedAspects: {
      handler() {
        this.loadAllProperties();
      },
    },
  },
  mounted() {
    this.loadAvailableAspects();
  },
  methods: {
    isFieldOptional(data) {
      if (data.type === "field") {
        const field = this.fields.find((f) => f.name === data.value);
        if (field && field.optional === true) {
          data.required = false;
          return true;
        }
      }
      return false;
    },
    async loadAvailableAspects() {
      try {
        const aspectsResponse = await alfrescoModelService.getAspects({
          maxItems: 1000,
        });
        if (aspectsResponse.list?.entries) {
          this.availableAspects = aspectsResponse.list.entries.map((entry) => ({
            id: entry.entry.id,
            title: entry.entry.title || entry.entry.id,
          }));
        }
      } catch (error) {
        console.error("Failed to load available aspects", error);
        this.availableAspects = [];
      }
    },
    async loadAllProperties() {
      const allProperties = [];

      // Load properties from the document type
      if (this.editableNature.type) {
        try {
          const typeResponse = await alfrescoModelService.getType(
            this.editableNature.type,
          );
          const typeProperties = alfrescoModelService.extractPropertiesFromType(
            typeResponse.entry,
          );
          allProperties.push(
            ...typeProperties.map((prop) => ({
              ...prop,
              source: `Type: ${this.editableNature.type}`,
            })),
          );
        } catch (error) {
          console.error(
            "Failed to load properties for type",
            this.editableNature.type,
            error,
          );
        }
      }

      // Load properties from selected aspects
      if (this.selectedAspects && this.selectedAspects.length > 0) {
        for (const aspectId of this.selectedAspects) {
          try {
            const aspectResponse =
              await alfrescoModelService.getAspect(aspectId);
            const aspectProperties =
              alfrescoModelService.extractPropertiesFromAspect(
                aspectResponse.entry,
              );
            allProperties.push(
              ...aspectProperties.map((prop) => ({
                ...prop,
                source: `Aspect: ${aspectId}`,
              })),
            );
          } catch (error) {
            console.error(
              "Failed to load properties for aspect",
              aspectId,
              error,
            );
          }
        }
      }

      this.availableProperties = allProperties;

      // Auto-generate editableMetadata from available properties
      this.generateMetadataFromProperties();
    },
    generateMetadataFromProperties() {
      // Get existing metadata configuration to preserve user settings
      const existingMetadata = {};
      this.editableMetadata.forEach((meta) => {
        existingMetadata[meta.key] = {
          type: meta.type,
          value: meta.value,
          required: meta.required,
          title: meta.title,
        };
      });

      // Generate metadata list from available properties
      const newMetadata = this.availableProperties.map((prop) => {
        // If metadata already exists, preserve its configuration
        if (existingMetadata[prop.key]) {
          return {
            key: prop.key,
            title: prop.title, // Use the fresh title from properties
            type: existingMetadata[prop.key].type,
            value: existingMetadata[prop.key].value,
            required: existingMetadata[prop.key].required || false,
          };
        }

        // Otherwise, create new metadata with default "ignored" type
        return {
          key: prop.key,
          title: prop.title,
          type: "ignored",
          value: "",
          required: false,
        };
      });

      // Also preserve metadata that was configured but is no longer in available properties
      // (this can happen if user changes document type or removes aspects)
      this.editableMetadata.forEach((meta) => {
        if (
          meta.type !== "ignored" &&
          !this.availableProperties.find((p) => p.key === meta.key)
        ) {
          newMetadata.push({
            key: meta.key,
            title: meta.title || meta.key,
            type: meta.type,
            value: meta.value,
            required: meta.required,
          });
        }
      });

      this.editableMetadata = newMetadata;
    },
    searchProperties(event) {
      const query = event.query.toLowerCase();
      if (!query) {
        this.filteredProperties = this.availableProperties;
      } else {
        this.filteredProperties = this.availableProperties.filter(
          (prop) =>
            prop.key.toLowerCase().includes(query) ||
            prop.title.toLowerCase().includes(query) ||
            (prop.source && prop.source.toLowerCase().includes(query)),
        );
      }
    },
    metadataToObjectArray(metadata) {
      if (!metadata) {
        this.editableMetadata = [];
        return;
      }

      // Store existing metadata configuration temporarily
      const existingConfig = Object.entries(metadata).reduce(
        (acc, [key, valueObj]) => {
          const type = Object.keys(valueObj).find((k) => k !== "required");
          const value = valueObj[type];
          const required = valueObj.required || false;
          acc[key] = { type, value, required };
          return acc;
        },
        {},
      );

      // If we have available properties, regenerate from them
      // This will be called again after loadAllProperties() completes
      if (this.availableProperties.length > 0) {
        this.editableMetadata = this.availableProperties.map((prop) => {
          const config = existingConfig[prop.key] || {
            type: "ignored",
            value: "",
            required: false,
          };
          return {
            key: prop.key,
            title: prop.title,
            type: config.type,
            value: config.value,
            required: config.required,
          };
        });
      } else {
        // Fallback: use metadata keys without titles (will be updated when properties load)
        this.editableMetadata = Object.entries(metadata).map(
          ([key, valueObj]) => {
            const type = Object.keys(valueObj).find((k) => k !== "required");
            const value = valueObj[type];
            const required = valueObj.required || false;
            return { key, type, value, required, title: key };
          },
        );
      }
    },
    metadataArrayToObject() {
      return this.editableMetadata.reduce((obj, meta) => {
        // Skip metadata with type "ignored"
        if (meta.key && meta.type !== "ignored") {
          obj[meta.key] = { [meta.type]: meta.value };
          if (
            meta.required &&
            meta.type !== "parent" &&
            meta.type !== "template"
          ) {
            obj[meta.key].required = true;
          }
        }
        return obj;
      }, {});
    },
    onMetadataTypeChange(meta) {
      // When type is changed to "field", auto-fill value with the property name without prefix
      if (meta.type === "field" && meta.key && !meta.value) {
        // Extract property name after the last colon (e.g., "cm:title" -> "title")
        const parts = meta.key.split(":");
        if (parts.length > 1) {
          meta.value = parts[parts.length - 1];
        } else {
          meta.value = meta.key;
        }
      }
    },
    addMetadata() {
      this.editableMetadata.push({
        key: "",
        type: "value",
        value: "",
        required: false,
      });
    },
    removeMetadata(index) {
      this.editableMetadata.splice(index, 1);
    },
    submit() {
      if (!this.editableNature.code || !this.editableNature.name) {
        this.toast.add({
          severity: "warn",
          summary: this.t("natureForm.validation.nameAndCodeRequired"),
          life: 4000,
        });
        return;
      }

      if (!this.isEditMode || this.isDuplicating) {
        const codeExists = this.existingNatures.some(
          (n) => n.code === this.editableNature.code,
        );
        if (codeExists) {
          this.toast.add({
            severity: "warn",
            summary: this.t("natureForm.validation.codeAlreadyExists", {
              code: this.editableNature.code,
            }),
            life: 4000,
          });
          return;
        }
      }

      const finalNature = {
        ...this.editableNature,
        aspects: this.selectedAspects,
        metadata: this.metadataArrayToObject(),
      };

      this.$emit("save", finalNature);
    },
  },
};
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-weight: 600;
}

.form-help {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-style: italic;
}

.metadata-table :deep(.p-datatable-header-cell) {
  font-size: 0.75rem;
}
</style>
